import React from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import moment from 'moment';
import AuditLogRow from 'components/Monitor/ActivityTab/AuditLogRow';
import PaginationJump from 'components/BiteDB/common/PaginationJump';
import type { ScopedQuery } from 'api/bitedb/shared';

/** One active filter, shown as a removable chip under the search bar. */
export type AuditFilterChip = {
  key: string;
  label: string;
  onRemove: () => void;
};

export type AuditActivity = {
  user: { firstName: string; lastName: string };
  action: string;
  timeStamp: string;
};

type AuditLogPanelProps = {
  state: ScopedQuery<AuditActivity[]>;
  currentPage: number;
  totalPages: number;
  onPrevPage: () => void;
  onNextPage: () => void;
  onJumpToPage?: (page: number) => void;
  emptyMessage?: string;
  idleMessage?: string;
  widerScopeLabel?: string;
  onViewWiderScope?: () => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  hasActiveFilters?: boolean;
  onOpenFilters?: () => void;
  filterChips?: AuditFilterChip[];
  onClearFilters?: () => void;
  appearance?: 'default' | 'entity-hub';
};

const activityIcon = (action: string) => {
  const normalized = action.toLowerCase();
  if (normalized.includes('status')) return <EditOutlinedIcon />;
  if (normalized.includes('ownership')) return <GroupsOutlinedIcon />;
  if (normalized.includes('hidden') || normalized.includes('false positive')) {
    return <VisibilityOffOutlinedIcon />;
  }
  if (normalized.includes('added') || normalized.includes('created')) {
    return <AddIcon />;
  }
  return <RefreshOutlinedIcon />;
};

const compactRelativeTime = (value: string) => {
  const date = moment(value);
  if (!date.isValid()) return '';
  const minutes = Math.max(0, moment().diff(date, 'minutes'));
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = moment().diff(date, 'hours');
  if (hours < 24) return `${hours}h ago`;
  const days = moment().diff(date, 'days');
  if (days < 14) return `${days}d ago`;
  const weeks = moment().diff(date, 'weeks');
  if (weeks < 8) return `${weeks}w ago`;
  return date.format('MMM D, YYYY');
};

const messageStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '20px',
};

const AuditLogPanel: React.FC<AuditLogPanelProps> = ({
  state,
  currentPage,
  totalPages,
  onPrevPage,
  onNextPage,
  onJumpToPage,
  emptyMessage = 'No activity recorded for this record yet.',
  idleMessage = 'Select a record to see its activity.',
  widerScopeLabel = 'View database activity',
  onViewWiderScope,
  searchValue,
  onSearchChange,
  hasActiveFilters = false,
  onOpenFilters,
  filterChips = [],
  onClearFilters,
  appearance = 'default',
}) => {
  const widerScopeLink = onViewWiderScope ? (
    <div style={{ marginTop: '8px' }}>
      <span
        className='activity-cont-frame-pagination-action-text'
        style={{ cursor: 'pointer' }}
        onClick={onViewWiderScope}
      >
        {widerScopeLabel}
      </span>
    </div>
  ) : null;

  if (appearance === 'entity-hub') {
    return (
      <div className='entity-hub-activity-panel'>
        {(onSearchChange || onOpenFilters) && (
          <div className='entity-hub-activity-toolbar'>
            {onSearchChange ? (
              <label className='entity-hub-activity-search'>
                <SearchOutlinedIcon aria-hidden='true' />
                <input
                  type='search'
                  aria-label='Search activity'
                  placeholder='Search activity'
                  value={searchValue || ''}
                  onChange={(event) => onSearchChange(event.target.value)}
                />
              </label>
            ) : null}
            {onOpenFilters ? (
              <button
                type='button'
                className={`entity-hub-activity-filter-btn${
                  hasActiveFilters ? ' selected' : ''
                }`}
                aria-label='Filter activity'
                aria-pressed={hasActiveFilters}
                onClick={onOpenFilters}
              >
                <FilterListIcon />
                Filters
              </button>
            ) : null}
          </div>
        )}
        {filterChips.length > 0 ? (
          <div className='entity-hub-activity-chips'>
            {filterChips.map((chip) => (
              <span className='entity-hub-activity-chip' key={chip.key}>
                <span title={chip.label}>{chip.label}</span>
                <button
                  type='button'
                  aria-label={`Remove filter ${chip.label}`}
                  onClick={chip.onRemove}
                >
                  <CloseIcon />
                </button>
              </span>
            ))}
            {onClearFilters ? (
              <button
                type='button'
                className='entity-hub-activity-chips-clear'
                onClick={onClearFilters}
              >
                Clear all
              </button>
            ) : null}
          </div>
        ) : null}
        <div className='entity-hub-activity-table'>
          <div className='entity-hub-activity-head'>
            <span>Event</span>
            <span>Source</span>
            <span>When</span>
          </div>
          <div className='entity-hub-activity-body'>
            {state.status === 'idle' ? (
              <div className='entity-hub-drawer-state'>{idleMessage}</div>
            ) : state.status === 'loading' ? (
              <div className='entity-hub-drawer-state'>Loading activity...</div>
            ) : state.status === 'error' ? (
              <div className='entity-hub-drawer-state error'>
                <span>Could not load activity.</span>
                <button type='button' onClick={state.retry}>
                  Retry
                </button>
              </div>
            ) : state.status === 'empty' && hasActiveFilters ? (
              <div className='entity-hub-drawer-state'>
                <span>No activity matches these filters.</span>
                {onClearFilters ? (
                  <button type='button' onClick={onClearFilters}>
                    Clear filters
                  </button>
                ) : null}
              </div>
            ) : state.status === 'empty' ? (
              <div className='entity-hub-drawer-state'>{emptyMessage}</div>
            ) : (
              state.data.map((activity, index) => {
                const source = [
                  activity.user?.firstName,
                  activity.user?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ');
                const action =
                  typeof activity.action === 'string'
                    ? activity.action.replace(/not_reviewed/g, 'not reviewed')
                    : String(activity.action || 'Activity recorded');

                return (
                  <div
                    className='entity-hub-activity-row'
                    key={`${activity.timeStamp}-${index}`}
                  >
                    <span className='entity-hub-activity-event-icon'>
                      {activityIcon(action)}
                    </span>
                    <strong title={action}>{action}</strong>
                    <span className='entity-hub-activity-source' title={source}>
                      {source || 'System'}
                    </span>
                    <time dateTime={activity.timeStamp}>
                      {compactRelativeTime(activity.timeStamp)}
                    </time>
                  </div>
                );
              })
            )}
          </div>
          {onViewWiderScope ? (
            <button
              type='button'
              className='entity-hub-activity-scope-link'
              onClick={onViewWiderScope}
            >
              {widerScopeLabel} →
            </button>
          ) : null}
        </div>

        {totalPages > 1 ? (
          <div className='entity-hub-activity-pagination'>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type='button'
              onClick={onPrevPage}
              disabled={currentPage <= 1}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={onNextPage}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className='relationship-tab-panel'>
      <div
        className='activity-cont-frame-right'
        style={{
          paddingTop: '5px',
          marginBottom: '20px',
          width: '100%',
        }}
      >
        <div className='activity-cont-frame-left-table'>
          <div className='activity-cont-frame-left-table-cont'>
            <div className='activity-cont-frame-left-table-cont-text-sup'>
              <div className='activity-cont-frame-left-table-cont-text-sup-bagde'>
                <div className='activity-cont-frame-left-table-cont-text-sup-bagde-frame'>
                  <div>Audit Log</div>
                </div>
              </div>
            </div>
          </div>
          {(onSearchChange || onOpenFilters) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                alignSelf: 'stretch',
                width: '100%',
                boxSizing: 'border-box',
                padding: '0 12px',
                marginBottom: '12px',
                marginTop: '12px',
              }}
            >
              {onSearchChange && (
                <input
                  type='text'
                  placeholder='Search activity'
                  value={searchValue || ''}
                  onChange={(event) => onSearchChange(event.target.value)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: '1px solid #2E3036',
                    background: 'var(--Background-200, var(--bg-colors))',
                    color: 'var(--text-primary)',
                    fontSize: '13px',
                  }}
                />
              )}
              {onOpenFilters && (
                <div
                  className={`audit-btn-star${hasActiveFilters ? ' selected' : ''}`}
                  onClick={onOpenFilters}
                  title='Filter'
                  style={{ cursor: 'pointer', flexShrink: 0 }}
                >
                  <FilterListIcon
                    sx={{
                      fontSize: 20,
                      color: hasActiveFilters
                        ? 'var(--Primary-400, #4895ef)'
                        : 'var(--text-classify)',
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <div className='audit-doc-tabe-col'>
            <div className='audit-doc-tabe-col-right-cell'>
              <div>
                <div className='audit-doc-tabe-col-left-cell-text'>User</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M7.99992 3.33301V12.6663M7.99992 12.6663L12.6666 7.99967M7.99992 12.6663L3.33325 7.99967'
                    stroke='var(--alternative-text-color)'
                    strokeWidth='1.33333'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            <div className='audit-doc-tabe-col-right-cell'>
              <div>
                <div className='audit-doc-tabe-col-left-cell-text'>Action</div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M7.99992 3.33301V12.6663M7.99992 12.6663L12.6666 7.99967M7.99992 12.6663L3.33325 7.99967'
                    stroke='var(--alternative-text-color)'
                    strokeWidth='1.33333'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
            <div className='audit-doc-tabe-col-right-cell'>
              <div>
                <div className='audit-doc-tabe-col-left-cell-text'>
                  Date & Time
                </div>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M7.99992 3.33301V12.6663M7.99992 12.6663L12.6666 7.99967M7.99992 12.6663L3.33325 7.99967'
                    stroke='var(--alternative-text-color)'
                    strokeWidth='1.33333'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
            </div>
          </div>
          <div
            className='activity-cont-frame-left-table-feed'
            style={{
              paddingTop: '15px',
              gap: 'var(--spacing-lg, 15px)',
            }}
          >
            {state.status === 'idle' && (
              <div className='hit-meta' style={messageStyle}>
                {idleMessage}
              </div>
            )}
            {state.status === 'loading' && (
              <div className='hit-meta' style={messageStyle}>
                Loading audit logs...
              </div>
            )}
            {state.status === 'error' && (
              <div
                className='hit-meta'
                style={{ ...messageStyle, color: 'var(--low-color, #dc2626)' }}
              >
                <div>Could not load audit logs.</div>
                <div style={{ marginTop: '8px' }}>
                  <span
                    className='activity-cont-frame-pagination-action-text'
                    style={{ cursor: 'pointer' }}
                    onClick={state.retry}
                  >
                    Retry
                  </span>
                </div>
              </div>
            )}
            {state.status === 'empty' && (
              <div className='hit-meta' style={messageStyle}>
                <div>{emptyMessage}</div>
                {widerScopeLink}
              </div>
            )}
            {state.status === 'ready' &&
              state.data.map((activity, index) => (
                <React.Fragment key={index}>
                  <AuditLogRow activity={activity} />
                  <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
                </React.Fragment>
              ))}
          </div>
          <div className='activity-cont-frame-pagination'>
            <div className='activity-cont-frame-pagination-text'>
              Page {currentPage} of {totalPages}
            </div>
            <div className='activity-cont-frame-pagination-action'>
              <div className='activity-cont-frame-pagination-action-prev'>
                <div
                  className='activity-cont-frame-pagination-action-text'
                  onClick={onPrevPage}
                >
                  Previous
                </div>
              </div>
              <div className='activity-cont-frame-pagination-action-prev'>
                <div
                  className='activity-cont-frame-pagination-action-text'
                  onClick={onNextPage}
                >
                  Next
                </div>
              </div>
            </div>
            {onJumpToPage && (
              <PaginationJump
                totalPages={totalPages}
                currentPage={currentPage}
                onJump={onJumpToPage}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogPanel;
